import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  AlertOctagon, Trash2, Database, ShieldAlert, CheckSquare, Square,
  RotateCcw, Clock, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { ResetBackups } from "@/components/ResetBackups";

const LEVEL = {
  high: { color: "border-red-500/30 bg-red-500/5 text-red-300", badge: "bg-red-500/15 text-red-300 border-red-500/30" },
  medium: { color: "border-amber-500/30 bg-amber-500/5 text-amber-300", badge: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  low: { color: "border-slate-700 bg-slate-900/40 text-slate-300", badge: "bg-slate-800 border-slate-700 text-slate-400" },
};

const CONFIRM_TOKEN = "DELETE-MY-DATA-NOW";

export default function DataReset() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [executing, setExecuting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [typedName, setTypedName] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => {
    api.get("/admin/reset/groups").then((r) => setGroups(r.data));
    api.get("/admin/reset/history").then((r) => setHistory(r.data));
    setRefreshKey((k) => k + 1);
  };
  useEffect(() => { load(); api.get("/setup/company-profile").then((r) => setCompanyName(r.data?.name || "")).catch(() => {}); }, []);
  const nameOk = !companyName || typedName.trim().toLowerCase() === companyName.trim().toLowerCase();

  const toggle = (key) => {
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(groups.map((g) => g.key)));
  const selectNone = () => setSelected(new Set());

  const selectedCount = groups.filter((g) => selected.has(g.key)).reduce((s, g) => s + g.total_documents, 0);
  const hasHighRisk = groups.some((g) => selected.has(g.key) && g.destructive_level === "high");

  const execute = async () => {
    if (confirmText !== CONFIRM_TOKEN) return toast.error("Confirmation phrase does not match");
    setExecuting(true);
    try {
      const r = await api.post("/admin/reset/wipe", {
        groups: Array.from(selected),
        confirm_token: CONFIRM_TOKEN,
        typed_company_name: typedName,
      });
      setLastResult(r.data);
      toast.success(`Deleted ${r.data.grand_total_deleted} records · backup saved`);
      setSelected(new Set());
      setShowConfirm(false);
      setConfirmText("");
      setTypedName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally { setExecuting(false); }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="data-reset-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-red-600/15 border border-red-500/30 flex items-center justify-center">
          <AlertOctagon className="w-6 h-6 text-red-400" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-red-400 font-bold mb-1">Admin · Danger Zone</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Data Reset Center</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Selectively wipe groups of data for <span className="text-red-300 font-semibold">your company only</span>.
            Other tenants are unaffected. This action is <strong className="text-red-300">irreversible</strong> — there is no undo.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
        <div className="text-xs text-red-200 leading-relaxed">
          <strong>Multi-tenant safety</strong>: deletions only touch records tagged with your <code className="px-1 bg-red-500/15 rounded">company_id</code>.
          Every wipe is logged to the <code className="px-1 bg-red-500/15 rounded">audit_logs</code> collection with your name and timestamp.
          The confirmation phrase <code className="px-1 bg-red-500/15 rounded">{CONFIRM_TOKEN}</code> is required.
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Resettable Groups · {groups.length}</div>
        <div className="flex gap-2">
          <Button data-testid="select-all-btn" variant="outline" size="sm" onClick={selectAll} className="border-slate-700 bg-slate-900/40 text-xs h-7">Select all</Button>
          <Button data-testid="select-none-btn" variant="outline" size="sm" onClick={selectNone} className="border-slate-700 bg-slate-900/40 text-xs h-7">Clear</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {groups.map((g) => {
          const lvl = LEVEL[g.destructive_level];
          const checked = selected.has(g.key);
          return (
            <button
              key={g.key}
              data-testid={`group-${g.key}`}
              onClick={() => toggle(g.key)}
              className={`text-left rounded-xl border p-4 transition-all ${checked ? "border-red-500/50 bg-red-500/10 ring-2 ring-red-500/20" : lvl.color + " hover:border-slate-600"}`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {checked
                    ? <CheckSquare className="w-5 h-5 text-red-400" />
                    : <Square className="w-5 h-5 text-slate-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="font-display text-sm font-semibold text-slate-100">{g.label}</div>
                    <Badge className={`${lvl.badge} text-[9px] uppercase tracking-wide`}>{g.destructive_level}</Badge>
                  </div>
                  <div className="text-xs text-slate-400 leading-relaxed">{g.description}</div>
                  <div className="text-[10px] font-mono text-slate-500 mt-2">
                    {g.total_documents.toLocaleString()} records · collections: {g.collections.join(", ")}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selected.size > 0 && (
        <div className="sticky bottom-4 z-10">
          <div className={`rounded-xl border-2 ${hasHighRisk ? "border-red-500/50 bg-red-950/80" : "border-amber-500/40 bg-amber-950/40"} backdrop-blur-xl p-4 flex items-center gap-4`}>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-100">
                {selected.size} group{selected.size > 1 ? "s" : ""} selected · <span className="font-mono text-red-300">{selectedCount.toLocaleString()} records</span> will be deleted
              </div>
              <div className="text-xs text-slate-400 mt-0.5">This action is irreversible. Confirmation required in next step.</div>
            </div>
            <Button
              data-testid="execute-wipe-btn"
              onClick={() => setShowConfirm(true)}
              className="bg-red-600 hover:bg-red-500 text-white font-semibold"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Wipe Selected
            </Button>
          </div>
        </div>
      )}

      {lastResult && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5" data-testid="wipe-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckSquare className="w-5 h-5 text-emerald-400" />
            <div className="text-sm font-semibold text-emerald-300">
              Wipe complete · {lastResult.grand_total_deleted.toLocaleString()} records removed
            </div>
          </div>
          <div className="space-y-2">
            {lastResult.summary.map((s, i) => (
              <div key={i} className="rounded-lg bg-slate-900/40 border border-slate-800 p-3 text-xs">
                <div className="font-semibold text-slate-200">{s.label || s.group}: {s.total_deleted ?? "—"} deleted</div>
                {s.details && (
                  <div className="mt-1 font-mono text-slate-500">
                    {s.details.map((d) => `${d.collection}=${d.deleted}`).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <ResetBackups refreshKey={refreshKey} />

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-1.5"><Clock className="w-3 h-3" />Wipe History · {history.length}</div>
        {history.length === 0 ? (
          <div className="text-center text-slate-500 py-6 text-sm">No prior wipes</div>
        ) : (
          <div className="divide-y divide-slate-800/60">
            {history.map((h, idx) => (
              <div key={idx} data-testid={`history-${idx}`} className="py-2.5 flex items-center gap-3">
                <RotateCcw className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-300 truncate">
                    <strong className="font-mono text-red-300">{h.grand_total_deleted}</strong> records · {h.groups.join(", ")}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">by {h.actor_name} ({h.actor_email})</div>
                </div>
                <div className="text-[10px] font-mono text-slate-600 shrink-0">{new Date(h.at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="bg-[#0B0410] border-red-500/40 max-w-lg" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300">
              <AlertTriangle className="w-5 h-5" />
              Final Confirmation Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-200">
              You're about to <strong>permanently delete</strong> {selectedCount.toLocaleString()} record(s) across {selected.size} group(s).
              Other tenants are unaffected. This <strong>cannot be undone</strong>.
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                Type <code className="px-1.5 py-0.5 bg-slate-800 text-red-300 rounded font-mono">{CONFIRM_TOKEN}</code> to proceed
              </label>
              <Input
                data-testid="confirm-input"
                placeholder={CONFIRM_TOKEN}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && confirmText === CONFIRM_TOKEN && nameOk && execute()}
                className="mt-2 bg-[#030712] border-red-500/30 font-mono text-red-200"
              />
            </div>
            {companyName && (
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                  Digite o nome da empresa: <code className="px-1.5 py-0.5 bg-slate-800 text-red-300 rounded font-mono">{companyName}</code>
                </label>
                <Input
                  data-testid="confirm-company-input"
                  placeholder={companyName}
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  className="mt-2 bg-[#030712] border-red-500/30 font-mono text-red-200"
                />
              </div>
            )}
            <div className="flex gap-2">
              <Button
                data-testid="confirm-execute-btn"
                onClick={execute}
                disabled={confirmText !== CONFIRM_TOKEN || !nameOk || executing}
                className="flex-1 bg-red-600 hover:bg-red-500 disabled:bg-slate-800 disabled:text-slate-600 text-white font-semibold"
              >
                {executing ? "Wiping..." : <><Trash2 className="w-4 h-4 mr-2" />Confirm Wipe</>}
              </Button>
              <Button
                data-testid="confirm-cancel-btn"
                variant="outline"
                onClick={() => { setShowConfirm(false); setConfirmText(""); }}
                className="border-slate-700 bg-slate-900/40 text-slate-300"
              >Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
